import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import dayjs from 'dayjs'
import { Gantt, Willow } from '@svar-ui/react-gantt'
import '@svar-ui/react-gantt/all.css'
import {
    buildMainTaskUpdateBodyFromTask,
    updateTaskSupport,
    MAIN_TASK_LIST_SORT,
} from '../taskRefactored/tasks.service'
import { useDispatch, useSelector } from 'react-redux'
import { projectsState } from './projects.slice'
import { useParams } from 'react-router-dom'
import AssigneeSelectorButton from './components/AssigneeSelectorButton'
import StatusSelector, {
    getCapsuleStyle,
} from '../taskRefactored/components/statusSelector'
// Reuse the selector's own capsule styling so the static chip and the live
// component are pixel-identical — the chip is only swapped out on click.
import statusStyles from '../taskRefactored/components/statusSelector/statusSelector.module.css'
import {
    getAssigneeColor as sharedAssigneeColor,
    getAssigneeInitials as sharedAssigneeInitials,
    resolveAssigneeName,
} from './components/assigneeAvatar'
import { Icon } from '@iconify/react'
import { Spin } from 'antd'
import {
    mainTaskList,
    mainTaskUpdate,
    taskListSupport,
    tasksState,
} from '../taskRefactored/tasks.slice'
import ProjectRoadMapSkeleton from './ProjectRoadMapSkeleton'
import styles from './ProjectRoadMap.module.css'

const findTaskInLanes = (lanes = [], taskId) => {
    if (!taskId) return null
    for (const lane of lanes) {
        for (const task of lane?.list || []) {
            if (task?._id === taskId) return task
            for (const subTask of task?.subTasks || []) {
                if (subTask?._id === taskId) return subTask
            }
        }
    }
    return null
}
/**
 * API stores inclusive calendar dates (last day is included).
 * SVAR Gantt uses start + duration (end is exclusive).
 * Always bridge through duration so drag/resize survives refresh.
 */
const toInclusiveDay = (value) => {
    const d = dayjs(value)
    return d.isValid() ? d.startOf('day') : null
}

/** Gantt requires a start. Due-date-only tasks must sit on the due date, not today. */
const resolveGanttSchedule = (task) => {
    const startDay = toInclusiveDay(task?.start)
    const endDay = toInclusiveDay(task?.end)

    if (startDay && endDay) {
        return {
            startDay,
            duration: Math.max(1, endDay.diff(startDay, 'day') + 1),
        }
    }
    if (endDay) {
        return { startDay: endDay, duration: 1 }
    }
    if (startDay) {
        return { startDay, duration: 1 }
    }
    return { startDay: dayjs().startOf('day'), duration: 1 }
}

const toApiDateValue = (value) => {
    const day = toInclusiveDay(value)
    return day ? day.valueOf() : null
}

/**
 * Convert API data to SVAR Gantt tasks with optional tree.
 */
const mapApiToGanttTree = (data = []) => {
    const tasks = []
    const links = []
    const visitedIds = new Set()

    const toGanttTask = (task, laneId, parentId = null) => {
        const { startDay, duration } = resolveGanttSchedule(task)

        return {
            id: task?._id,
            text: task?.title?.trim() || 'Untitled Task',
            start: startDay.toDate(),
            // Let Gantt derive exclusive end from duration (do not pass API inclusive end)
            duration,
            type: 'task',
            assignee: task?.assigneeId || null,
            status: task?.status || laneId,
            ...(parentId ? { parent: parentId } : {}),
        }
    }

    data.forEach((lane) => {
        const laneTasks = lane.list || []

        laneTasks.forEach((task) => {
            if (!task?._id || visitedIds.has(task?._id)) return

            // Skip nested child rows from root level if backend also returns them in lane.list.
            if (
                Array.isArray(task?.parentTask) &&
                task?.parentTask?.length > 0
            ) {
                return
            }

            const parentTask = toGanttTask(task, lane._id)
            tasks.push(parentTask)
            visitedIds.add(task?._id)

            if (Array.isArray(task?.subTasks) && task?.subTasks.length > 0) {
                task?.subTasks.forEach((subTask) => {
                    if (!subTask?._id || visitedIds.has(subTask?._id)) return
                    const childTask = toGanttTask(subTask, lane._id, task?._id)
                    tasks.push(childTask)
                    visitedIds.add(subTask?._id)
                })
            }
        })
    })

    return { tasks, links }
}

const getInclusiveEndFromGanttTask = (task) => {
    const startDay = toInclusiveDay(task?.start)
    if (!startDay) return null

    const duration = Math.max(1, Number(task?.duration) || 1)
    return startDay.add(duration - 1, 'day')
}

const RoadmapCellUiContext = React.createContext({
    updatingStatusId: null,
    updatingAssigneeId: null,
    statusTypeOptions: [],
    onStatusChange: null,
    onAssigneeChange: null,
    onTaskClick: null,
    projectId: null,
    taskListKind: 'support',
})

/* SVAR re-renders the grid as it scrolls and hands each cell a freshly built
   `row` object, so a bare React.memo never hits — the shallow prop compare
   fails on identity every time and every cell re-renders on every scroll frame.
   That is what makes the assignee avatar and task title visibly flicker.
   Compare the specific fields a cell actually reads instead. */
const memoRowCell = (Component, fields) =>
    React.memo(Component, (prev, next) =>
        fields.every((field) => prev.row?.[field] === next.row?.[field])
    )

/* Static stand-in for AssigneeSelectorButton's trigger. Deliberately holds no
   state and runs no effects: the real button resets an imageError flag from a
   mount effect, which flashes the initials before the avatar settles every time
   the row re-renders. The image hides itself via the DOM on error, revealing the
   initials underneath, so a broken avatar still costs no React work. */
const StaticAssignee = ({ assignee, onActivate }) => {
    const name = resolveAssigneeName(assignee)
    const hasAssignee = Boolean(name)
    const profilePicture = assignee?.profilePicture

    return (
        <div
            className={styles.assigneeStatic}
            onClick={onActivate}
            // Long names still ellipsise at this width; keep the full one reachable.
            title={name || undefined}
        >
            <span
                className={styles.assigneeAvatar}
                style={{
                    backgroundColor: hasAssignee
                        ? sharedAssigneeColor(assignee)
                        : 'transparent',
                    border: hasAssignee ? 'none' : '1px dashed #cbd5e1',
                }}
            >
                {hasAssignee ? (
                    <>
                        <span className={styles.assigneeInitials}>
                            {sharedAssigneeInitials(assignee)}
                        </span>
                        {profilePicture ? (
                            <img
                                className={styles.assigneeImg}
                                src={profilePicture}
                                alt={name}
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none'
                                }}
                            />
                        ) : null}
                    </>
                ) : (
                    <Icon
                        icon="mi:user"
                        width={16}
                        height={16}
                        style={{ color: '#94a3b8' }}
                    />
                )}
            </span>
            {hasAssignee ? (
                <span className={styles.assigneeName}>{name}</span>
            ) : null}
        </div>
    )
}

const resolveStatusValue = (status, statusTypeOptions = []) => {
    const match = statusTypeOptions.find(
        (option) => option?.value === status || option?.label === status
    )
    if (match) return match
    if (!status) return null
    return { label: status, value: status, color: 'cyan' }
}

const RoadmapStatusCell = memoRowCell(({ row }) => {
    const { updatingStatusId, statusTypeOptions, onStatusChange } =
        React.useContext(RoadmapCellUiContext)
    const isUpdating = updatingStatusId === row.id
    // Mount the real selector only once this cell is used — see StaticAssignee.
    const [active, setActive] = useState(false)

    /* Derive rather than mirror in state. resolveStatusValue returns a fresh
       object for any status not in statusTypeOptions, so the old mount effect
       set state with a new reference every time — a second render for every
       row the virtualiser mounted while scrolling. The optimistic update still
       happens: the parent's exec('update-task') writes the status onto the row
       synchronously before it awaits the request. */
    const value = useMemo(
        () => resolveStatusValue(row.status, statusTypeOptions),
        [row.status, statusTypeOptions]
    )

    const handleStatusUpdate = (_name, payload) => {
        const newStatus = payload?.value ?? payload?.label
        setActive(false)
        if (!newStatus || newStatus === (value?.value ?? value?.label)) return
        onStatusChange?.(row, newStatus)
    }

    // Matches the capsule variant: colour keys off the label, grey by default.
    const capsuleLabel = value?.label || ''
    const capsuleStyle = getCapsuleStyle(capsuleLabel)

    return (
        <div
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className={styles.statusCell}
        >
            {isUpdating ? (
                <Spin size="small" />
            ) : active ? (
                <StatusSelector
                    value={value}
                    statusType={statusTypeOptions}
                    name="status"
                    variant="capsule"
                    placeholder="Status"
                    updateDetails={handleStatusUpdate}
                    getPopupContainer={() => document.body}
                    defaultOpen
                />
            ) : (
                /* The capsule variant's own trigger, minus the Popover. */
                <button
                    type="button"
                    className={statusStyles.capsuleTrigger}
                    style={{
                        background: capsuleStyle.bg,
                        color: capsuleStyle.color,
                    }}
                    onClick={() => setActive(true)}
                >
                    <span className={statusStyles.capsuleTriggerText}>
                        {capsuleLabel || 'Status'}
                    </span>
                </button>
            )}
        </div>
    )
}, ['id', 'status'])
RoadmapStatusCell.displayName = 'RoadmapStatusCell'

const RoadmapAssignedCell = memoRowCell(({ row }) => {
    const { updatingAssigneeId, onAssigneeChange, projectId, taskListKind } =
        React.useContext(RoadmapCellUiContext)
    const isLoading = updatingAssigneeId === row.id
    const [active, setActive] = useState(false)

    return (
        <div
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className={styles.assigneeCell}
        >
            {isLoading ? (
                <Spin size="small" />
            ) : active ? (
                <AssigneeSelectorButton
                    selectedAssignee={row.assignee}
                    onSelect={(assigneeId) => {
                        setActive(false)
                        onAssigneeChange?.(row, assigneeId)
                    }}
                    showName
                    projectId={projectId}
                    taskListKind={taskListKind}
                    getAssigneeColor={getAssigneeColor}
                    getInitials={getInitials}
                    parentComponent="gantt"
                    open={active}
                    onOpenChange={(open) => {
                        if (!open) setActive(false)
                    }}
                />
            ) : (
                <StaticAssignee
                    assignee={row.assignee}
                    onActivate={() => setActive(true)}
                />
            )}
        </div>
    )
}, ['id', 'assignee'])
RoadmapAssignedCell.displayName = 'RoadmapAssignedCell'

const RoadmapTaskCell = memoRowCell(({ row }) => {
    const { onTaskClick } = React.useContext(RoadmapCellUiContext)
    return (
        <span
            className={styles.taskCellText}
            onClick={() => onTaskClick?.(row.id)}
        >
            {row.text}
        </span>
    )
}, ['id', 'text'])
RoadmapTaskCell.displayName = 'RoadmapTaskCell'

const getAssigneeColor = (name) => {
    if (!name) return '#e2e8f0'
    const colors = [
        '#85BBF9',
        '#6366f1',
        '#8b5cf6',
        '#ec4899',
        '#f59e0b',
        '#10b981',
        '#06b6d4',
    ]
    const index = name.charCodeAt(0) % colors.length
    return colors[index]
}

const getInitials = (name) => {
    if (!name) return 'U'
    const parts = name.split(' ')
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
}

/* Widths are sized for the UPPERCASE headers (see --wx-grid-header-text-transform
   in the stylesheet): too narrow and SVAR ellipsises the header itself.

   Never set `flexgrow` here. SVAR's normalizeColumns does `width = flexgrow ? 1
   : width`, so declaring it collapses that column to 1px. Its ensureVisibleFlex
   already grants flexgrow to the `text` column after normalization — i.e. after
   the width is resolved — which is the only way to get both. */
const ROADMAP_COLUMNS = [
    {
        id: 'text',
        header: 'Task',
        width: 240,
        resize: true,
        cell: RoadmapTaskCell,
    },
    {
        id: 'status',
        header: 'Status',
        width: 120,
        align: 'center',
        resize: true,
        cell: RoadmapStatusCell,
    },
    {
        id: 'assignee',
        header: 'Assigned',
        width: 180,
        // Left-aligned on purpose: names vary in length, and centring a
        // variable-width block costs horizontal room at both edges. The header
        // stays centred via the .wx-header rule in the stylesheet.
        resize: true,
        cell: RoadmapAssignedCell,
        sort: false,
    },
    {
        id: 'duration',
        // "DURATION" ellipsises at this width once uppercased; the cell values
        // already read "3 days", so the shorter header loses nothing.
        header: 'Days',
        width: 90,
        align: 'center',
        resize: true,
        template: (v) => `${v} day${v > 1 ? 's' : ''}`,
    },
    {
        id: 'start',
        header: 'Start',
        width: 110,
        align: 'center',
        resize: true,
        template: (v) => (v ? dayjs(v).format('DD MMM YY') : '—'),
    },
    {
        id: 'end',
        header: 'End',
        width: 110,
        align: 'center',
        resize: true,
        // Gantt end is exclusive — show inclusive last day in the grid
        template: (v) =>
            v ? dayjs(v).subtract(1, 'day').format('DD MMM YY') : '—',
    },
]

const ROADMAP_SCALES = [
    { unit: 'month', step: 1, format: '%F %Y' },
    { unit: 'week', step: 1, format: 'Week %W' },
    { unit: 'day', step: 1, format: '%j' },
]

const MIN_GANTT_HEIGHT = 240

/* These must be handed to the Gantt, not just assumed: SVAR defaults to
   cellHeight 38 and scaleHeight 36 *per scale row*, so leaving them implicit
   made the surface-height maths below wrong by the header plus 2px a row. */
const ROW_HEIGHT = 36
const SCALE_ROW_HEIGHT = 32
const HEADER_HEIGHT = SCALE_ROW_HEIGHT * ROADMAP_SCALES.length
/* Each grid row draws a 1px bottom border (--wx-gantt-border) that sits outside
   cellHeight, so N rows occupy N*(ROW_HEIGHT+1), not N*ROW_HEIGHT. Leaving it
   out is what clipped the last row. Plus the surface's own 1px top and bottom.
   Erring a pixel or two over is invisible; under it cuts a row in half. */
const ROW_BORDER = 1
const SURFACE_BORDER = 2
/* SVAR still draws scale rules and a scroll gutter outside cellHeight /
   scaleHeight. With one filtered row the raw sum is ~135px, which crushes the
   3-tier header so month/week/day and TASK/STATUS overlap. */
const SVAR_LAYOUT_CHROME = 28
/* Enough placeholder rows to read as a populated grid without implying a count. */
const SKELETON_ROW_COUNT = 8
/* Just enough to clear the viewport edge. The Board/List/Roadmap/Sprint
   switcher floats over the bottom-centre by design; reserving a full-width
   band for it left a dead strip across the whole width instead. */
const VIEWPORT_GUTTER = 24

const ProjectRoadMap = ({
    apiData,
    onTaskClick,
    projectId: projectIdProp,
    taskListKind = 'support',
    onRefreshTasks,
}) => {
    const dispatch = useDispatch()
    const isMainList = taskListKind === 'main'

    const apiRef = useRef(null)
    const [ganttApi, setGanttApi] = useState(null)

    const { id: routeId } = useParams()
    const projectId = projectIdProp ?? routeId

    const { tasks, links } = useMemo(
        () => mapApiToGanttTree(apiData),
        [apiData]
    )

    const { projectStatusList } = useSelector(projectsState)

    const { projectTaskListStatus, projectMainTaskListStatus } =
        useSelector(tasksState)

    /* Only for the first load. A background refresh keeps the current chart on
       screen rather than replacing it with placeholders. */
    const isInitialLoading =
        (isMainList
            ? projectMainTaskListStatus
            : projectTaskListStatus) === 'loading'

    const statusTypeOptions = useMemo(() => {
        if (projectStatusList?.length) {
            return projectStatusList.map((status) => {
                if (status && typeof status === 'object') {
                    return {
                        label: status.label ?? status.name ?? status._id,
                        value: status.value ?? status._id ?? status.label,
                        color: status.color || 'cyan',
                    }
                }
                return {
                    label: status,
                    value: status,
                    color: 'cyan',
                }
            })
        }
        return (apiData || [])
            .map((lane) => {
                if (!lane?._id) return null
                return {
                    label: lane._id,
                    value: lane._id,
                    color: lane?.color || 'cyan',
                }
            })
            .filter(Boolean)
    }, [projectStatusList, apiData])

    const refreshTaskList = useCallback(() => {
        if (!projectId) return
        // Prefer parent refresh so active toolbar search/filters are preserved
        if (typeof onRefreshTasks === 'function') {
            // Post-mutation sync — not a landing/filter/search load.
            onRefreshTasks({}, { silent: true })
            return
        }
        if (isMainList) {
            dispatch(
                mainTaskList({
                    projectId,
                    skip: 0,
                    limit: 10,
                    sort: MAIN_TASK_LIST_SORT,
                })
            )
            return
        }
        dispatch(
            taskListSupport({
                skip: 0,
                limit: 10,
                filter: { projectId },
                listType: 'status',
            })
        )
    }, [dispatch, projectId, isMainList, onRefreshTasks])

    const [updatingAssigneeId, setUpdatingAssigneeId] = useState(null)
    const [updatingStatusId, setUpdatingStatusId] = useState(null)

    /* A roadmap is hard to read without a "you are here" line, especially when
       the plan starts months from today. Built once per mount — a new Date on
       every render would give the Gantt a changing prop for no reason. */
    const markers = useMemo(
        () => [{ start: new Date(), text: 'Today' }],
        []
    )


    const [startDate, endDate] = useMemo(() => {
        if (!tasks.length) return [new Date(), new Date()]

        const starts = tasks.map((t) => t.start.getTime())
        const ends = tasks.map((t) =>
            dayjs(t.start)
                .add(Math.max(1, Number(t.duration) || 1), 'day')
                .valueOf()
        )

        const minStart = new Date(Math.min(...starts))
        const maxEnd = new Date(Math.max(...ends))
        maxEnd.setMonth(maxEnd.getMonth() + 1) // padding

        return [minStart, maxEnd]
    }, [tasks])

    //     if (!row.assignee) return "-";

    //     const { name, profilePicture } = row.assignee;
    //     const firstLetter = name?.charAt(0).toUpperCase();

    //     return (
    //         <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    //             {profilePicture ? (
    //                 <img
    //                     src={profilePicture}
    //                     alt={name}
    //                     style={{
    //                         width: 24,
    //                         height: 24,
    //                         borderRadius: "50%",
    //                         objectFit: "cover"
    //                     }}
    //                 />
    //             ) : (
    //                 <div
    //                     style={{
    //                         width: 24,
    //                         height: 24,
    //                         borderRadius: "50%",
    //                         backgroundColor: "#1677ff",
    //                         color: "#fff",
    //                         display: "flex",
    //                         alignItems: "center",
    //                         justifyContent: "center",
    //                         fontSize: 12,
    //                         fontWeight: 600
    //                     }}
    //                 >
    //                     {firstLetter}
    //                 </div>
    //             )}

    //             <span>{name}</span>
    //         </div>
    //     );
    // };

    // const StatusCell = ({ row }) => {
    //     const colors = {
    //         Done: { bg: "#DCFCE7", color: "#166534" },
    //         "In Progress": { bg: "#DBEAFE", color: "#1E40AF" },
    //         Pending: { bg: "#FEF3C7", color: "#92400E" }
    //     };

    //     const style = colors[row.status] || {
    //         bg: "#E5E7EB",
    //         color: "#374151"
    //     };

    //     return (
    //         <span
    //             style={{
    //                 padding: "4px 10px",
    //                 borderRadius: 12,
    //                 fontSize: 12,
    //                 fontWeight: 500,
    //                 background: style.bg,
    //                 color: style.color
    //             }}
    //         >
    //             {row.status}
    //         </span>
    //     );
    // };
    const handleAssigneeChange = useCallback(
        async (row, assigneeId) => {
            try {
                setUpdatingAssigneeId(row.id)

                apiRef.current?.exec('update-task', {
                    id: row.id,
                    task: {
                        type: row.type || 'task',
                        start: row.start,
                        end: row.end,
                        duration: row.duration,
                        text: row.text,
                        status: row.status,
                        assignee: assigneeId,
                    },
                })

                if (isMainList) {
                    const sourceTask = findTaskInLanes(apiData, row.id)
                    if (sourceTask) {
                        const body = buildMainTaskUpdateBodyFromTask(
                            sourceTask,
                            projectId,
                            {
                                assigneeId: assigneeId?._id ?? assigneeId,
                            }
                        )
                        await dispatch(
                            mainTaskUpdate({
                                id: row.id,
                                body,
                            })
                        )
                        refreshTaskList()
                    }
                } else {
                    const res = await updateTaskSupport({
                        _id: row.id,
                        updateTask: {
                            assigneeId: assigneeId._id,
                            projectId,
                        },
                    })

                    if (res.data.success) {
                        refreshTaskList()
                    }
                }
            } catch (err) {
                console.error('Assignee update failed', err)
            } finally {
                setUpdatingAssigneeId(null)
            }
        },
        [apiData, dispatch, isMainList, projectId, refreshTaskList]
    )

    const handleStatusChange = useCallback(
        async (row, newStatus) => {
            if (!row?.id || !newStatus) return
            setUpdatingStatusId(row.id)

            apiRef.current?.exec('update-task', {
                id: row.id,
                task: {
                    type: row.type || 'task',
                    start: row.start,
                    end: row.end,
                    duration: row.duration,
                    text: row.text,
                    status: newStatus,
                    assignee: row.assignee,
                },
            })

            try {
                if (isMainList) {
                    const sourceTask = findTaskInLanes(apiData, row.id)
                    if (sourceTask) {
                        const body = buildMainTaskUpdateBodyFromTask(
                            sourceTask,
                            projectId,
                            { status: newStatus }
                        )
                        await dispatch(mainTaskUpdate({ id: row.id, body }))
                        refreshTaskList()
                    }
                } else {
                    const res = await updateTaskSupport({
                        _id: row.id,
                        updateTask: { status: newStatus, projectId },
                    })
                    if (res.data.success) {
                        refreshTaskList()
                    }
                }
            } catch (err) {
                console.error('Status update failed', err)
            } finally {
                setUpdatingStatusId(null)
            }
        },
        [apiData, dispatch, isMainList, projectId, refreshTaskList]
    )

    const cellUiValue = useMemo(
        () => ({
            updatingStatusId,
            updatingAssigneeId,
            statusTypeOptions,
            onStatusChange: handleStatusChange,
            onAssigneeChange: handleAssigneeChange,
            onTaskClick,
            projectId,
            taskListKind,
        }),
        [
            updatingStatusId,
            updatingAssigneeId,
            statusTypeOptions,
            handleStatusChange,
            handleAssigneeChange,
            onTaskClick,
            projectId,
            taskListKind,
        ]
    )

    const updateTaskOnDreg = useCallback(
        async (id, start, end) => {
            const apiStart = toApiDateValue(start)
            const apiEnd = toApiDateValue(end)
            if (!id || apiStart == null || apiEnd == null) return

            try {
                if (isMainList) {
                    const sourceTask = findTaskInLanes(apiData, id)
                    if (!sourceTask) return
                    const body = buildMainTaskUpdateBodyFromTask(
                        sourceTask,
                        projectId,
                        { start: apiStart, end: apiEnd }
                    )
                    await dispatch(mainTaskUpdate({ id, body }))
                    refreshTaskList()
                    return
                }
                const res = await updateTaskSupport({
                    _id: id,
                    updateTask: {
                        projectId: projectId,
                        start: apiStart,
                        end: apiEnd,
                    },
                })
                if (res?.data?.success === false) {
                    console.error('Failed to update task dates', res?.data)
                    return
                }
                refreshTaskList()
            } catch (error) {
                console.error('Failed to update task dates', error)
            }
        },
        [projectId, isMainList, apiData, dispatch, refreshTaskList]
    )

    const initGantt = useCallback((api) => {
        apiRef.current = api
        setGanttApi(api)
    }, [])

    useEffect(() => {
        if (!ganttApi) return

        const onUpdateTask = ({ id, diff }) => {
            // Chart drag/resize commits dates via update-task + diff.
            // Skip assignee/status updates and no-op drags (no diff).
            if (typeof diff !== 'number' || diff === 0) return

            const nextTask = ganttApi.getTask?.(id)
            if (!id || !nextTask?.start) return

            const inclusiveEnd = getInclusiveEndFromGanttTask(nextTask)
            if (!inclusiveEnd) return

            updateTaskOnDreg(id, nextTask.start, inclusiveEnd.toDate())
        }

        ganttApi.detach?.('roadmap-date-sync')
        ganttApi.on('update-task', onUpdateTask, { tag: 'roadmap-date-sync' })

        return () => {
            ganttApi.detach?.('roadmap-date-sync')
        }
    }, [ganttApi, updateTaskOnDreg])

    const surfaceRef = useRef(null)
    const [availableHeight, setAvailableHeight] = useState(0)

    /* Measure the chart surface itself, not the page wrapper, so the space
       above it is not counted as available.

       Rounded and compared before writing: this sets the surface's height, and
       getBoundingClientRect returns sub-pixel values, so an unguarded setState
       can re-render for a fractional change and keep nudging the size that
       SVAR's own ResizeObserver is watching. */
    useLayoutEffect(() => {
        const updateHeight = () => {
            const node = surfaceRef.current
            if (!node) return
            const rect = node.getBoundingClientRect()
            const next = Math.max(
                MIN_GANTT_HEIGHT,
                Math.round(window.innerHeight - rect.top - VIEWPORT_GUTTER)
            )
            setAvailableHeight((prev) => (prev === next ? prev : next))
        }

        updateHeight()
        window.addEventListener('resize', updateHeight)
        return () => window.removeEventListener('resize', updateHeight)
    }, [tasks.length])

    /* Take the viewport when there is more data than fits, but never more than
       the rows need. The chart paints its day columns for the full height of
       its container regardless of row count, so an over-tall surface draws an
       empty grid under the last task that reads as blank rows.

       Count root tasks, not tasks.length: subtasks are in `tasks` but SVAR
       collapses them by default, so counting them padded the surface by a row
       per hidden child. Expanding a parent makes this an underestimate, which
       the Gantt simply absorbs by scrolling — far better than dead space.

       Accurate only because cellHeight/scaleHeight are passed to the Gantt
       above; otherwise SVAR's own 38/36 defaults would apply here. */
    const visibleRowCount = useMemo(
        () => tasks.filter((task) => !task.parent).length,
        [tasks]
    )

    const contentHeight =
        HEADER_HEIGHT +
        visibleRowCount * (ROW_HEIGHT + ROW_BORDER) +
        SURFACE_BORDER +
        SVAR_LAYOUT_CHROME

    /* Card sizes to its content, capped at the viewport so long lists scroll
       internally rather than pushing the page down. Empty space below is just
       page background — a content-sized card on a scrollable page, which is
       the pattern most modern dashboards use. Never shrink below the header
       plus one row: a single filter match used to collapse the timescale. */
    const minChartHeight =
        HEADER_HEIGHT +
        (ROW_HEIGHT + ROW_BORDER) +
        SURFACE_BORDER +
        SVAR_LAYOUT_CHROME

    const ganttHeight = Math.max(
        minChartHeight,
        availableHeight ? Math.min(contentHeight, availableHeight) : contentHeight
    )

    /* Ahead of the empty state: with no rows yet the two are indistinguishable,
       and "Nothing scheduled yet" while still fetching is simply wrong. */
    if (isInitialLoading && !tasks.length) {
        return (
            <div className={styles.roadmapPage}>
                <ProjectRoadMapSkeleton
                    height={
                        HEADER_HEIGHT +
                        SKELETON_ROW_COUNT * (ROW_HEIGHT + ROW_BORDER) +
                        SURFACE_BORDER
                    }
                    rowHeight={ROW_HEIGHT}
                    headerHeight={HEADER_HEIGHT}
                    rows={SKELETON_ROW_COUNT}
                />
            </div>
        )
    }

    if (!tasks.length) {
        return (
            <div className={styles.roadmapPage}>
                {/* Same ref as the chart surface, so the measuring effect runs
                    in this branch too and the panel can fill the space below
                    the tabs — otherwise it hugs its content at the top. */}
                <div
                    ref={surfaceRef}
                    className={styles.emptyState}
                    style={{ minHeight: availableHeight || undefined }}
                >
                    <div className={styles.emptyStateTitle}>
                        Nothing scheduled yet
                    </div>
                    <p className={styles.emptyStateText}>
                        Tasks with a start and due date appear here as a
                        timeline you can drag to reschedule.
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.roadmapPage}>
            <RoadmapCellUiContext.Provider value={cellUiValue}>
                <Willow>
                    <div
                        ref={surfaceRef}
                        className={styles.ganttSurface}
                        style={{ height: ganttHeight }}
                    >
                        <Gantt
                            tasks={tasks}
                            links={links}
                            columns={ROADMAP_COLUMNS}
                            scales={ROADMAP_SCALES}
                            start={startDate}
                            end={endDate}
                            cellHeight={ROW_HEIGHT}
                            scaleHeight={SCALE_ROW_HEIGHT}
                            markers={markers}
                            collapsible
                            init={initGantt}
                            ref={apiRef}
                        />
                    </div>
                </Willow>
            </RoadmapCellUiContext.Provider>
        </div>
    )
}

export default ProjectRoadMap
